import { useMemo } from 'react';
import { Option } from '@trukt/portal-utils/Option';

type AccessKeys<T> = T extends object
  ? {
      [
        K in keyof T
      ]: `${Exclude<K, symbol>}${AccessKeys<T[K]> extends never ? '' : `.${AccessKeys<T[K]>}`}`;
    }[keyof T]
  : never;

type UseOptionsKeyMappers<T> = { label: AccessKeys<T>; value: AccessKeys<T> };

const getKey = <T>(source: T, path: AccessKeys<T>): string => {
  const keys = path.split('.');
  let store: unknown = source;

  for (const key of keys) {
    if (store === null || store === undefined) {
      return '';
    }

    if (typeof store === 'object' && key in store) {
      store = store[key as keyof typeof store];
    }
  }
  return String(store);
};

export const EMPTY_DATA = [];

export const useOptions = <T extends object>(
  data: T[] = EMPTY_DATA,
  keyMappers: UseOptionsKeyMappers<T>,
  customLabelBuilder?: (x: T) => string,
): Option<string, T>[] => {
  return useMemo(() => {
    return data.map((x) => {
      return {
        label: customLabelBuilder?.(x) ?? String(getKey(x, keyMappers.label)),
        value: String(getKey(x, keyMappers.value)),
        raw: x,
      };
    });
  }, [customLabelBuilder, data, keyMappers.label, keyMappers.value]);
};
